<template>
    <AuthLayout>
        <div>
            <div
                class="flex w-full flex-col items-stretch rounded-[8px] bg-white px-[22px] pt-[42px] pb-[42px] sm:max-w-[374px] rounded-[12.8px] border border-[#E5E7EB]"
            >
                <div
                    class="mb-5 flex h-[21px] flex-col justify-center self-stretch text-center"
                >
                    <h1
                        class="font-inter text-[21px] font-bold leading-[100%] tracking-tight"
                    >
                        {{ t("vue.create_new_account") }}
                    </h1>
                </div>

                <form @submit.prevent="submit">
                    <div class="mb-4">
                        <p
                            class="mb-[6px] text-xs font-medium text-gray-700 text-center"
                        >
                            {{ t("vue.sign_up_with") }}
                        </p>

                        <div class="grid grid-cols-2 gap-4">
                            <SocialiteButton
                                icon-only
                                provider="google"
                                label="Google"
                                url="google.auth.index"
                                :invitation-token="invitationToken"
                            />

                            <SocialiteButton
                                icon-only
                                provider="microsoft"
                                label="Microsoft"
                                url="microsoft.auth.index"
                                :invitation-token="invitationToken"
                            />
                        </div>
                    </div>

                    <div class="my-5 flex items-center gap-3 text-[#AFB5BF]">
                        <div class="h-px flex-1 bg-[#AFB5BF]"></div>
                        <span
                            class="text-[8.5px] font-semibold tracking-wider text-[#AFB5BF]"
                        >
                            {{ t("vue.or_with_the_email") }}
                        </span>
                        <div class="h-px flex-1 bg-[#AFB5BF]"></div>
                    </div>

                    <div class="space-y-5">
                        <TextInput
                            v-model="state.form.email"
                            type="email"
                            autocomplete="email"
                            name="email"
                            required
                            suppress-required-icon
                            autofocus
                            :label="t('vue.email_address')"
                            :placeholder="
                                t('vue.enter_your_email_address_you_example')
                            "
                            :error="getError('email')"
                            @blur="touchIfNotEmpty('email')"
                        />

                        <TextInput
                            v-model="state.form.name"
                            type="text"
                            autocomplete="name"
                            name="name"
                            required
                            suppress-required-icon
                            :label="t('vue.your_name')"
                            :placeholder="t('vue.enter_your_name_example')"
                            :error="getError('name')"
                            @blur="touchIfNotEmpty('name')"
                        />

                        <TextInput
                            v-model="state.form.password"
                            type="password"
                            autocomplete="new-password"
                            name="password"
                            required
                            suppress-required-icon
                            :label="t('vue.password')"
                            :placeholder="t('vue.set_your_password')"
                            :error="getError('password')"
                            @blur="touchIfNotEmpty('password')"
                        />

                        <TextInput
                            v-model="state.form.password_confirmation"
                            type="password"
                            autocomplete="new-password"
                            name="password_confirmation"
                            required
                            suppress-required-icon
                            :label="t('vue.confirm_password')"
                            :placeholder="t('vue.your_password_again_please')"
                            :error="getError('password_confirmation')"
                            @blur="touchIfNotEmpty('password_confirmation')"
                        />
                    </div>

                    <div class="mt-[24px]">
                        <AtomicButton
                            color="yellow"
                            size="md"
                            class="h-8 px-4 w-full font-normal font-[10.5px] font-semibold leading-4 cursor-pointer"
                            :disabled="loading"
                            type="submit"
                        >
                            {{ t("vue.create_account") }}
                        </AtomicButton>

                        <p
                            class="mt-[24px] text-center text-[10.5px] text-[#9FA6B2]"
                        >
                            {{ t("vue.by_continuing_auth_agreement_text") }}
                            <a
                                class="text-[#9FA6B2] underline hover:text-custom-blue-600"
                                href="https://dmarceye.com/terms-of-service"
                                target="_blank"
                            >
                                {{ t("vue.terms_of_service_7th_case") }}
                            </a>
                            {{ t("vue.and") }}
                            <a
                                class="text-[#9FA6B2] underline hover:text-custom-blue-600"
                                href="https://dmarceye.com/privacy-policy"
                                target="_blank"
                            >
                                {{ t("vue.privacy_policy_7th_case") }} </a
                            >.
                        </p>

                        <p
                            class="mt-[24px] text-center text-[12.25px] text-gray-500"
                        >
                            {{ t("vue.already_have_an_account") }}
                            <inertia-link
                                :href="route('login')"
                                class="text-[12.25px] font-medium text-custom-blue-500 hover:text-custom-blue-600"
                            >
                                {{ t("vue.sign_in_challenge") }}
                            </inertia-link>
                        </p>
                    </div>
                </form>
            </div>
        </div>
    </AuthLayout>

    <Head title="DMARCeye.com registration - DMARC monitoring & reporting">
        <meta
            name="description"
            content="Create your DMARCeye account today and start protecting your domain from spoofing. Set up DMARC monitoring and view reports in minutes."
        />
    </Head>
</template>

<script lang="ts" setup>
import { Head } from "@inertiajs/vue3";
import route from "ziggy-js";
import { useReCaptcha } from "vue-recaptcha-v3";
import { computed, reactive, ref } from "vue";
import {
    required,
    email as emailRule,
    maxLength,
    minLength,
    helpers,
    sameAs,
} from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import { router } from "@inertiajs/vue3";
import { useTranslations } from "@/composables/useTranslations";

const props = defineProps({
    email: {
        required: false,
        type: String,
        default: "",
    },
    invitationToken: {
        required: false,
        type: String,
        default: "",
    },
});

// Get access to translations
const { t } = useTranslations();

const reCaptcha = useReCaptcha();

interface IForm {
    form: {
        name: string;
        email: string;
        password: string;
        password_confirmation: string;
        terms: boolean;
        recaptcha_token: string;
        invitation_token: string;
        hubspotutk: string | null;
    };
}

const state: IForm = reactive({
    form: {
        name: "",
        email: props.email ?? "",
        password: "",
        password_confirmation: "",
        terms: true,
        recaptcha_token: "",
        invitation_token: props.invitationToken,
        hubspotutk: null,
    },
});

const passwordRef = computed(() => state.form.password);

const rules = {
    form: {
        name: {
            required,
            maxLength: maxLength(255),
        },
        email: {
            required,
            emailRule,
            maxLength: maxLength(255),
        },
        password: {
            required,
            minLength: minLength(8),
            maxLength: maxLength(255),
        },
        password_confirmation: {
            required,
            minLength: minLength(8),
            maxLength: maxLength(255),
            sameAsPassword: helpers.withMessage(
                t("vue.passwords_do_not_match"),
                sameAs(passwordRef),
            ),
        },
    },
};

const v$ = useVuelidate(rules, state);
const errors = ref({});
const loading = ref(false);

const touchIfNotEmpty = (
    field: "name" | "email" | "password" | "password_confirmation",
) => {
    const rawValue = state.form[field];
    const value =
        typeof rawValue === "string" ? rawValue.trim() : (rawValue ?? "");

    if (value !== "") {
        // @ts-ignore
        v$.value.form[field].touch();
    }
};

const hubspotutk = ref<string | null>(getHubspotUTK());

function getHubspotUTK() {
    const match = document.cookie.match(new RegExp("(^| )hubspotutk=([^;]+)"));
    return match ? match[2] : null;
}

const submit = async () => {
    v$.value.form.touch();
    if (v$.value.form.$error) {
        () => undefined;
    } else {
        loading.value = true;
        let token = undefined;

        try {
            await reCaptcha?.recaptchaLoaded();
            token = await reCaptcha?.executeRecaptcha("register");

            if (token === undefined) {
                throw new Error();
            }
        } catch (e) {
            loading.value = false;
            errors.value = {
                email: t("vue.recaptcha_validation_failed"),
            };
            return;
        }
        state.form.recaptcha_token = token;
        state.form.hubspotutk = hubspotutk.value ?? undefined;

        router.post(route("register"), state.form, {
            preserveScroll: true,
            onError: (err) => {
                console.log(err);
                errors.value = err;
            },
            onFinish: () => {
                loading.value = false;
                state.form.password = "";
                state.form.password_confirmation = "";
                v$.value.$reset();
            },
        });
    }
};

const getError = (field: string) => {
    // @ts-ignore
    return (
        v$.value.form[field]?.$errors[0]?.$message ||
        errors.value?.[field] ||
        ""
    );
};
</script>
